import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ItemContainer, ItemTitle, ItemContent, ItemDescription, ItemPrice } from "./styles";

import Offer from "types/Offer";
import generateCurrencyValue from "utils/generateCurrencyValue";

import { monetaryConfig } from "state/config-slice";
import FeatureWrapper from "components/FeatureToggle/FeatureWrapper";
import { isFeatureActive } from "utils/getFeature";
import { replaceSpacesAndSymbols } from "utils/replaceSpacesAndSymbols";

interface IPaymentItem {
  offer: Offer;
  isFromCheckout?: boolean;
  isNetflixOffer?: boolean;
}

const generateTrialSavingsString = (trialInfo: { amount: number; unit: string }) => {
  if (trialInfo.unit === "month") {
    return Number(trialInfo.amount) === 1
      ? t("1 Month Savings With Trial")
      : t("{{ count }} Months Savings With Trial", {
          count: trialInfo.amount,
        });
  }

  if (trialInfo.unit === "week") {
    return Number(trialInfo.amount) === 1
      ? t("1 Week Savings With Trial")
      : t("{{ count }} Weeks Savings With Trial", {
          count: trialInfo.amount,
        });
  }

  if (trialInfo.unit === "day") {
    return Number(trialInfo.amount) === 1
      ? t("1 Day Savings With Trial")
      : t("{{ count }} Days Savings With Trial", {
          count: trialInfo.amount,
        });
  }

  if (trialInfo.unit === "year") {
    return Number(trialInfo.amount) === 1
      ? t("1 Year Savings With Trial")
      : t("{{ count }} Years Savings With Trial", {
          count: trialInfo.amount,
        });
  }
};

const generateTrialSavingsValue = (
  trialInfo: { amount?: number; unit?: string; trialPrice?: number; recurring?: boolean },
  priceBeforeConversion: string,
  duration?: undefined | "WEEKLY" | "MONTHLY" | "QUARTERLY",
): number => {
  const price = Number(priceBeforeConversion);
  const trialPrice = trialInfo?.trialPrice || 0;
  const trialAmount = trialInfo?.amount ?? 1;
  const isOneTimePurchase = trialInfo?.recurring === false;
  const validatedTrialPrice = isOneTimePurchase ? trialPrice : trialPrice * trialAmount;

  if (trialInfo.unit === "month" || duration === "WEEKLY") {
    return price * trialAmount - validatedTrialPrice;
  }

  if (trialInfo.unit === "day") {
    return (price * trialAmount) / 30;
  }

  if (trialInfo.unit === "week") {
    return (price / 4) * trialAmount;
  }

  if (trialInfo.unit === "year") {
    return price * trialAmount * 12;
  }

  return price;
};

const PaymentItem = ({ offer, isFromCheckout = false, isNetflixOffer = false }: IPaymentItem) => {
  const { t } = useTranslation();
  const monetarySettings = useSelector(monetaryConfig);

  const isWeekly = offer?.duration?.name === "WEEKLY";

  return (
    <ItemContainer>
      <ItemTitle
        $isFromCheckout={isFromCheckout}
        data-testid={`payments-overview-trial-${replaceSpacesAndSymbols(offer?.marketplaceInfo?.title)}-title`}
      >
        {offer.marketplaceInfo.title}
      </ItemTitle>
      {offer.trial && offer.marketplaceInfo.trialInfo && (
        <>
          <ItemContent data-testid="payments-overview-trial-content">
            <ItemDescription
              className="payments-overview-trial-content"
              $isFromCheckout={isFromCheckout}
              data-testid="payments-overview-trial-desc"
            >
              {generateTrialSavingsString(offer.marketplaceInfo.trialInfo)}
            </ItemDescription>
            <ItemPrice $isFromCheckout={isFromCheckout} data-testid="payments-overview-trial-price">
              {generateCurrencyValue(
                generateTrialSavingsValue(
                  offer.marketplaceInfo.trialInfo,
                  offer.marketplaceInfo.price,
                  offer?.duration?.name,
                ),
                monetarySettings,
              )}
            </ItemPrice>
          </ItemContent>
          {offer.chargingMethod !== "NON_RECURRING" && (
            <ItemContent data-testid="payments-overview-trial-content-non-rec">
              <ItemDescription $isFromCheckout={isFromCheckout} data-testid="payments-overview-trial-desc-non-rec">
                {t(isWeekly ? "Weekly Cost After Trial Period" : "Monthly Cost After Trial Period")}
              </ItemDescription>
              <ItemPrice $isFromCheckout={isFromCheckout} data-testid="payments-overview-trial-price-non-rec">
                {generateCurrencyValue(offer.marketplaceInfo.price, monetarySettings)}
              </ItemPrice>
            </ItemContent>
          )}
        </>
      )}
      {!offer.trial && (
        <ItemContent data-testid="payments-overview-no-trial-content">
          <ItemDescription $isFromCheckout={isFromCheckout} data-testid="payments-overview-no-trial-cost-description">
            {t(isWeekly ? "Weekly Cost" : "Monthly Cost")}
            <FeatureWrapper name="Exclude netflix text">
              <>{isNetflixOffer && t(" - Please confirm subscription cost via Netflix Account")}</>
            </FeatureWrapper>
          </ItemDescription>
          <ItemPrice $isFromCheckout={isFromCheckout} data-testid={`${offer.partner}-price-text`}>
            {offer.marketplaceInfo.priceText
              ? t("{{ priceText }}", { priceText: offer.marketplaceInfo.priceText })
              : t(
                  isNetflixOffer && isFeatureActive("Exclude netflix text")
                    ? isWeekly
                      ? "From {{ price }}/wk."
                      : "From {{ price }}/mo."
                    : isWeekly
                    ? "{{ price }}/wk."
                    : "{{ price }}/mo.",
                  {
                    price: generateCurrencyValue(
                      offer.type === "hardbundle" ? 0 : offer.marketplaceInfo.price,
                      monetarySettings,
                    ),
                  },
                )}
          </ItemPrice>
        </ItemContent>
      )}
    </ItemContainer>
  );
};

export default PaymentItem;
